import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useEventDashboard, useEventSocket } from '../contexts/event';
import { eventKeys } from '../queries/keys';
import { readEventState, readCountdown, normalizeCountdown, pickFocal } from '../utils/state';
import Intro from '../phases/intro';
import Register from '../phases/register';
import Landing from '../phases/landing';
import Countdown from '../phases/countdown';
import BidPanel from '../phases/bid';

import '../styles/experience.css';

// TSS vite/03 — EXPERIENCIA del participante (QR → puja), mobile-first, dark.
// Máquina de fases: intro → registro → landing → LIVE. La sub-fase live es por
// TIEMPO según el bloque `countdown` del estado (contrato market): antes de
// countdown_at → waiting; en [countdown_at, opens_at) → countdown; desde opens_at
// → bid. Skew corregido con server_time. El WS siembra el estado (join_event ack
// + broadcasts market_event:state / market_event:countdown); el poll de 5s es
// respaldo. El participant_token se guarda por evento (localStorage) → un
// reingreso salta directo a live.

const pKey = (t) => `evento:${t}:participant`;
const loadP = (t) => { try { return JSON.parse(localStorage.getItem(pKey(t)) || 'null'); } catch { return null; } };
const saveP = (t, p) => { try { localStorage.setItem(pKey(t), JSON.stringify(p)); } catch { /* ignore */ } };

// Pre-countdown SIN número: la cuenta arranca de sorpresa (no anticipamos los
// segundos). Solo un "mantente listo" con pulso.
function Waiting() {
    return (
        <div className="evx__wait">
            <div className="evx__wait-pulse" aria-hidden="true" />
            <h2 className="evx__wait-title">Prepara tu dedo</h2>
            <p className="evx__wait-sub">La subasta está por comenzar. Mantente atento.</p>
        </div>
    );
}

export default function EventExperience({ token }) {
    const qc = useQueryClient();
    const { data, isError, error } = useEventDashboard(token);

    // Countdown recibido por el WS (fuente de verdad en vivo). setCd sólo se llama
    // desde el handler del socket (event handler, no un efecto). market_event:state
    // trae el snapshot completo (offerings + countdown) → lo escribimos directo en
    // la caché del dashboard (evita un GET por puja); market_event:countdown trae
    // sólo el bloque countdown. El join_event ack se enruta como un state (event.jsx).
    const [cd, setCd] = useState(null); // resultado de normalizeCountdown | null
    const onWsEvent = useCallback((evName, args) => {
        const payload = args?.[0];
        if (!payload) return;
        if (evName === 'market_event:state') {
            qc.setQueryData(eventKeys.dashboard(token), payload);
            if (payload.countdown) setCd(normalizeCountdown(payload.countdown));
        } else if (evName === 'market_event:countdown') {
            setCd(normalizeCountdown(payload));
        }
    }, [qc, token]);
    useEventSocket(token, undefined, onWsEvent);

    const { offerings, currentOfferingId } = useMemo(() => readEventState(data), [data]);
    // Vaca en foco = la que fijó el subastador (current_offering_id). Cambia al
    // instante cuando el server difunde el nuevo foco mid-puja.
    const focal = useMemo(() => pickFocal(offerings, currentOfferingId), [offerings, currentOfferingId]);

    // Respaldo por polling (sin efecto/setState): si el countdown llega por el GET
    // del dashboard, se deriva acá. El WS (cd) tiene prioridad cuando existe.
    const dashCd = useMemo(() => normalizeCountdown(readCountdown(data)), [data]);
    const countdown = cd ?? dashCd;

    // Diagnóstico (dev): estado del countdown resuelto (arma/abre).
    const cdArmed = countdown?.armed;
    const cdOpens = countdown?.opensAt;
    useEffect(() => {
        if (import.meta.env.DEV) console.debug('[evento] countdown:', { armed: cdArmed, opensAt: cdOpens });
    }, [cdArmed, cdOpens]);

    const [participant, setParticipant] = useState(() => loadP(token));
    const [step, setStep] = useState(() => (loadP(token) ? 'live' : 'intro'));

    // Reloj (100ms) para la sub-fase live por tiempo.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 100);
        return () => clearInterval(t);
    }, []);

    // Sub-fase live: waiting (agendado, sin número) → countdown [countdown_at,
    // opens_at) → bid. Antes de countdown_at la cuenta NO se anticipa (sorpresa).
    let live = 'waiting';
    let secondsLeft = 0;
    let progress = 0;
    if (countdown?.armed && countdown.opensAt != null) {
        if (now >= countdown.opensAt) {
            live = 'bid';
        } else if (countdown.countdownAt != null && now >= countdown.countdownAt) {
            live = 'countdown';
            secondsLeft = Math.max(1, Math.ceil((countdown.opensAt - now) / 1000));
            const total = (countdown.opensAt - countdown.countdownAt) || ((countdown.seconds || 20) * 1000);
            progress = Math.min(1, Math.max(0, (now - countdown.countdownAt) / total));
        }
    }

    if (isError && error?.status === 404) {
        return <div className="evx evx--msg">Evento no encontrado o ya cerrado.</div>;
    }

    const onRegistered = (p) => { setParticipant(p); saveP(token, p); setStep('landing'); };

    let content;
    if (step === 'intro') {
        content = <Intro onDone={() => setStep('register')} />;
    } else if (step === 'register') {
        content = <Register token={token} onDone={onRegistered} />;
    } else if (step === 'landing') {
        content = <Landing onDone={() => setStep('live')} />;
    } else if (live === 'countdown') {
        content = <Countdown secondsLeft={secondsLeft} progress={progress} />;
    } else if (live === 'bid' && focal) {
        // key por vaca: al cambiar el foco (subastador), el panel se remonta y
        // resetea el % local en vez de arrastrar el de la vaca anterior.
        content = <BidPanel key={focal.id} token={token} participant={participant} focal={focal} />;
    } else {
        content = <Waiting />;
    }

    return <div className="evx">{content}</div>;
}
